import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";

import isEqual from "lodash/isEqual";
import { useDispatch } from "react-redux";

import { useTranslate } from "@similarweb/i18n-provider";
import { Stack } from "@similarweb/ui-components/foundations";

import { showErrorToast } from "app/actions/toast_actions";
import useLatestValue from "app/custom-hooks/useLatestValue";
import { AcknowledgeModal } from "app/pages/sales-intelligence/common-components/AcknowledgeModal/AcknowledgeModal";
import { renderErrorToast } from "app/pages/sales-intelligence/common-components/ErrorToast/ErrorToast";
import { filterUsageMap } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterDefinition";
import { omitFiltersDeniedForUsage } from "app/pages/sales-intelligence/modules/leads-search-filters/utils/omitFiltersDeniedForUsage";
import { useTeamsTrackingService } from "app/pages/sales-intelligence/modules/teams/hooks/useTeamsTrackingService";
import { useUpdateTeamFiltersMutation } from "app/pages/sales-intelligence/modules/teams/queries/useUpdateTeamFiltersMutation";
import type { TeamFilters, TeamListItem } from "app/pages/sales-intelligence/modules/teams/types/teams.types";

import { TeamDetailTabFooter } from "../TeamDetailContainer/TeamDetailTabFooter";
import type { TeamDetailTabHandle } from "app/pages/sales-intelligence/pages/management/types/teamDetail.types";

import { TeamFiltersBody } from "./TeamFiltersBody";
import { useTeamFiltersBootstrap } from "./useTeamFiltersBootstrap";

export type TeamFiltersContainerHandle = TeamDetailTabHandle;

type Props = {
    team: TeamListItem;
    isNewTeam?: boolean;
};

export const TeamFiltersContainer = forwardRef<TeamFiltersContainerHandle, Props>(({ team, isNewTeam }, ref) => {
    const { translate } = useTranslate();
    const dispatch = useDispatch();
    const tracking = useTeamsTrackingService();

    useTeamFiltersBootstrap();

    const teamName = team.name ?? "unnamed";

    const savedFilters = useMemo<TeamFilters>(
        () => (team.filters && Object.keys(team.filters).length > 0 ? team.filters : {}),
        [team.filters],
    );

    const [draftFilters, setDraftFilters] = useState<TeamFilters>(savedFilters);
    const [hasTouched, setHasTouched] = useState(false);
    const [isSavedModalOpen, setIsSavedModalOpen] = useState(false);

    const draftFiltersRef = useLatestValue(draftFilters);
    const savedFiltersRef = useLatestValue(savedFilters);
    const hasTouchedRef = useLatestValue(hasTouched);

    useEffect(() => {
        if (!hasTouchedRef.current) {
            setDraftFilters(savedFilters);
        }
    }, [savedFilters]);

    const handleFiltersUpdate = useCallback((next: TeamFilters) => {
        setDraftFilters(next);
        setHasTouched(true);
    }, []);

    const { mutateAsync: updateTeamFilters, isPending: isSaving } = useUpdateTeamFiltersMutation();

    const handleSave = useCallback(() => {
        void (async () => {
            try {
                const queryFilters = omitFiltersDeniedForUsage(draftFiltersRef.current, filterUsageMap.territory);
                await updateTeamFilters({ teamId: team.teamId, queryFilters });
                tracking.trackFiltersSaved(teamName, queryFilters);
                // Adopt the persisted payload: the mutation's `onSuccess` refreshes `savedFilters` while
                // `hasTouched` is still true, so its reset effect is skipped and the draft would keep a
                // stripped key forever — leaving the unsaved-changes guard permanently armed.
                setDraftFilters(queryFilters);
                setHasTouched(false);
                setIsSavedModalOpen(true);
            } catch {
                dispatch(
                    showErrorToast(
                        renderErrorToast({
                            content: translate("si.common.toast.error"),
                            minWidth: "auto",
                        }),
                    ),
                );
            }
        })();
    }, [updateTeamFilters, team.teamId, draftFiltersRef, tracking, teamName, dispatch, translate]);

    const handleReset = useCallback(() => {
        setDraftFilters(savedFiltersRef.current);
        setHasTouched(false);
        tracking.trackFiltersReset(teamName);
    }, [savedFiltersRef, tracking, teamName]);

    const handleCloseSavedModal = useCallback(() => setIsSavedModalOpen(false), []);

    useImperativeHandle(
        ref,
        () => ({
            hasUnsavedChanges: () => !isEqual(draftFiltersRef.current, savedFiltersRef.current),
            discardChanges: () => {
                setDraftFilters(savedFiltersRef.current);
                setHasTouched(false);
            },
        }),
        [draftFiltersRef, savedFiltersRef],
    );

    const isActionDisabled = !hasTouched || isSaving;

    return (
        <>
            <Stack
                width="100%"
                gap="16px"
                data-automation="team-filters"
                flex="1"
                minHeight="0"
            >
                <Stack
                    minHeight="0"
                    flex="1"
                    data-automation="team-filters-card"
                >
                    <TeamFiltersBody
                        draftFilters={draftFilters}
                        onFiltersUpdate={handleFiltersUpdate}
                        startScrolledPastPermanentFilters={isNewTeam}
                    />
                </Stack>

                <TeamDetailTabFooter
                    onSave={handleSave}
                    onReset={handleReset}
                    isSaveDisabled={isActionDisabled}
                    isResetDisabled={isActionDisabled}
                    isSaving={isSaving}
                    saveLabelKey="si.management.teams.detail.filters.save_button"
                    resetLabelKey="si.management.teams.detail.filters.reset_button"
                    dataAutomationPrefix="team-filters"
                />
            </Stack>

            <AcknowledgeModal
                isOpen={isSavedModalOpen}
                title={translate("si.management.teams.detail.filters.saved_modal.title")}
                description={translate("si.management.teams.detail.filters.saved_modal.description")}
                dataAutomation="team-filters-saved-modal"
                onConfirm={handleCloseSavedModal}
            />
        </>
    );
});

TeamFiltersContainer.displayName = "TeamFiltersContainer";
